/**
 * useArrangeModeInteractions — L2 hook for pointer interactions in Arrange mode.
 *
 * Two behaviours, both delegated from a single root container:
 *   • hover  → publish the hovered compartment number into ProductContext so
 *              every UI surface (plan, CompartmentsPane, legend) can sync via
 *              the shared `hoveredCompartmentId` state. The renderer applies
 *              the CSS class on the matching path.
 *   • click  → if the compartment has alternative variants (`variantsCount > 1`)
 *              cycle to the next version. Otherwise no-op.
 *
 * Touch users skip hover (avoids a stray highlight flash before tap-to-click).
 *
 * All inputs are mirrored into refs so the returned handlers are stable
 * (`useCallback` deps empty) — re-renders from selectedVariants / hovered
 * state never recreate the handler functions.
 *
 * @param {object} params
 * @param {SVGElement|null} params.svgElement                 — triggers hover-ref reset on change
 * @param {number[]} params.variantsCount                     — indexed by (compartmentNumber - 1)
 * @param {(n: number|null, event?: PointerEvent) => void} params.setHoveredCompartmentId
 * @param {(n: number) => void} params.cycleCompartmentVariant
 *
 * @returns {{ handlePointerMove: function, handlePointerLeave: function, handleClick: function }}
 */

import { useCallback, useEffect, useRef } from 'react'

import {
  getCompartmentPath,
  getCompartmentNumber,
} from '../utils/svgInfoModeHelpers.js'

export function useArrangeModeInteractions({
  svgElement,
  variantsCount,
  setHoveredCompartmentId,
  cycleCompartmentVariant,
}) {
  // ── Hover-tracking ref — at most one compartment path highlighted at a time
  const hoveredPathRef = useRef(null)

  // ── Stable refs for callback-only reads ─────────────────────────────────
  const variantsCountRef = useRef(variantsCount)
  variantsCountRef.current = variantsCount

  const setHoveredCompartmentIdRef = useRef(setHoveredCompartmentId)
  setHoveredCompartmentIdRef.current = setHoveredCompartmentId

  const cycleCompartmentVariantRef = useRef(cycleCompartmentVariant)
  cycleCompartmentVariantRef.current = cycleCompartmentVariant

  // ── Reset hover ref when SVG is remounted (old DOM nodes are stale) ─────
  useEffect(() => {
    hoveredPathRef.current = null
  }, [svgElement])

  // ── Pointer move (hover) ────────────────────────────────────────────────
  const handlePointerMove = useCallback((e) => {
    if (e.pointerType === 'touch') return

    const path = getCompartmentPath(e.target)
    if (path === hoveredPathRef.current) return

    hoveredPathRef.current = path
    const n = path ? getCompartmentNumber(path) : null
    setHoveredCompartmentIdRef.current(n, e)
  }, [])

  // ── Pointer leave (container) ───────────────────────────────────────────
  const handlePointerLeave = useCallback(() => {
    if (!hoveredPathRef.current) return
    hoveredPathRef.current = null
    setHoveredCompartmentIdRef.current(null)
  }, [])

  // ── Click (cycle variant) ───────────────────────────────────────────────
  const handleClick = useCallback((e) => {
    const path = getCompartmentPath(e.target)
    if (!path) return
    const n = getCompartmentNumber(path)
    if (n == null) return

    const counts = variantsCountRef.current
    if (!(counts[n - 1] > 1)) return // no alternatives — no-op

    cycleCompartmentVariantRef.current(n)
  }, [])

  return { handlePointerMove, handlePointerLeave, handleClick }
}
